import { gql, useApolloClient, useMutation } from '@apollo/client';
import IconTrash from '@apollo/icons/default/IconTrash.svg';
import {
  Button,
  ButtonGroup,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import React, { useEffect, useRef, useState } from 'react';

import { Form } from 'src/components/common/form/Form';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

export function ChannelActionMenu({
  disabled,
  graphId,
  channel,
}: {
  disabled: boolean;
  graphId: string;
  channel: GraphQLTypes.ChannelCardChannelFragment;
}) {
  const client = useApolloClient();
  const [modalVisible, setModalVisible] = useState(false);
  const focusRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (modalVisible && focusRef.current) {
      focusRef.current.focus();
    }
  }, [modalVisible]);

  const [deleteChannel, { loading, error }] = useMutation<
    GraphQLTypes.ChannelActionMenuDeleteChannelMutation,
    GraphQLTypes.ChannelActionMenuDeleteChannelMutationVariables
  >(
    gql`
      mutation ChannelActionMenuDeleteChannelMutation(
        $graphId: ID!
        $channelId: ID!
      ) {
        service(id: $graphId) {
          deleteChannel(id: $channelId)
        }
      }
    `,
    {
      variables: { graphId, channelId: channel.id },
      onCompleted: () => {
        client.cache.evict({
          id: client.cache.identify({
            __typename: channel.__typename,
            id: channel.id,
          }),
        });
        setModalVisible(false);
      },
    },
  );

  if (error !== undefined) {
    throw new Error('Something went wrong trying to delete your channel...');
  }

  return (
    <>
      <IconButton
        aria-label="Delete channel"
        variant="destructive"
        isDisabled={disabled}
        icon={<IconTrash />}
        onClick={() => setModalVisible(true)}
      />
      <Modal
        isOpen={modalVisible}
        onClose={() => setModalVisible(false)}
        size="lg"
      >
        <ModalOverlay />
        <Form onSubmit={() => deleteChannel()} noValidate>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>Delete Channel?</ModalTitle>
            </ModalHeader>
            <ModalBody>
              <em>This action cannot be undone. Do you wish to continue?</em>
            </ModalBody>
            <ModalFooter>
              <ButtonGroup>
                <Button
                  variant="secondary"
                  ref={focusRef}
                  isDisabled={loading}
                  onClick={() => setModalVisible(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  isDisabled={loading}
                  type="submit"
                >
                  Delete
                </Button>
              </ButtonGroup>
            </ModalFooter>
          </ModalContent>
        </Form>
      </Modal>
    </>
  );
}
